/* global debounce */

if (!customElements.get("custom-search")) {
    class CustomSearch extends HTMLElement {
        constructor() {
            super();
            this.cachedResults = {};
            this.input = null;
            this.resetBtn = null;
            this.results = null;
            this.overlay = null;
            this.statusEl = null;
            this.loadingText = null;
            this.initialHtmlStored = false;
            this.modal = null;
            this.recentlyViewed = null;
        }

        connectedCallback() {
            // when DOM is ready find elements
            this.input = this.querySelector(".js-search-input");
            this.resetBtn = this.querySelector(".js-search-reset");
            this.results = this.querySelector(".js-search-results");
            this.overlay = this.querySelector(".js-search-overlay");
            this.statusEl = this.querySelector(".js-search-status");
            this.loadingText = this.getAttribute("data-loading-text");
            this.recentlyViewed = this.querySelector(".recently-viewed");
            this.modal = this.querySelector("[result-modal]");
            // store initial HTML (only once)
            if (
                !this.initialHtmlStored &&
                this.results &&
                this.results.innerHTML.trim()
            ) {
                const initialResults = this.results.innerHTML.replace(
                    /psearch/g,
                    this.input.id,
                );
                this.cachedResults["initialResults"] = initialResults;
                this.initialHtmlStored = true;
            }

            this.addListeners();
            if (this.recentlyViewed) this.getRecentlyViewedResults();
        }

        /**
         * Triggers when the web component is removed from the DOM
         */
        disconnectedCallback() {
            this.closeModal();
        }

        addListeners() {
            // Open modal on input focus/click
            this.input.addEventListener(
                "focus",
                this.handleInputFocus.bind(this),
            );
            this.input.addEventListener(
                "click",
                this.handleInputFocus.bind(this),
            );
            this.input.addEventListener(
                "input",
                debounce(this.handleInput.bind(this), 300),
            );

            // Handle form submit
            const form = this.querySelector("form");
            if (form) {
                form.addEventListener("submit", this.handleSubmit.bind(this));
            }

            const modalForm = this.modal?.querySelector("form");
            if (modalForm) {
                modalForm.addEventListener(
                    "submit",
                    this.handleModalSubmit.bind(this),
                );
            }

            // Handle modal close
            if (this.modal) {
                this.modal.addEventListener("click", (evt) => {
                    if (
                        evt.target === this.modal ||
                        evt.target.matches(".js-close-modal")
                    ) {
                        this.closeModal();
                    }
                });
            }
        }

        /**
         * Handles input focus/click - opens modal
         */
        handleInputFocus(evt) {
            evt.preventDefault();
            if (evt?.type === "click" && this.hasAttribute("open")) {
                //this.input.removeAttribute("data-no-focus");
            }
            this.openModal();
        }

        /**
         * Opens the search modal
         */
        openModal() {
            if (!this.modal || this.hasAttribute("open")) return;

            this.overlay.classList.add("is-visible");
            this.input.setAttribute("aria-expanded", "true");

            if (!window.matchMedia("(pointer: fine)").matches) {
                this.input.setAttribute("data-no-focus", "");
                this.input.blur();
            }

            this.setAttribute("open", "");
            if (this.getQuery().length) this.resetBtn.hidden = false;
            document.body.classList.add("overlay-predictive-search");

            this.overlayClickHandler =
                this.overlayClickHandler || this.closeModal.bind(this);
            this.resetBtnClickHandler =
                this.resetBtnClickHandler || this.closeModal.bind(this);
            // Add escape key handler
            this.keyupHandler =
                this.keyupHandler ||
                ((evt) => {
                    if (evt.key === "Escape") {
                        this.closeModal();
                    }
                });

            // Add focus trap handler
            //this.focusTrapHandler = this.focusTrapHandler || this.handleFocusTrap.bind(this);
            //this.addEventListener("keydown", this.focusTrapHandler);

            // focus out handler
            this.focusOutHandler =
                this.focusOutHandler || this.handleFocusOut.bind(this);
            this.addEventListener("focusout", this.focusOutHandler);

            document.addEventListener("keyup", this.keyupHandler);
            this.overlay.addEventListener("click", this.overlayClickHandler);
            this.resetBtn.addEventListener("click", this.resetBtnClickHandler);
        }

        /**
         * Closes the search modal
         */
        closeModal(evt) {
            if (!this.modal) return;

            if (evt && evt.target === this.resetBtn) {
                this.input.value = "";
                this.removeAttribute("results");
                //this.input.focus();

                // Clear results
                if (this.results && this.cachedResults?.initialResults) {
                    this.results.innerHTML = this.cachedResults?.initialResults;
                }
            }

            this.removeAttribute("open");
            this.overlay.classList.remove("is-visible");
            this.input.setAttribute("aria-activedescendant", "");
            this.input.setAttribute("aria-expanded", "false");
            document.body.classList.remove("overlay-predictive-search");

            const selected = this.querySelector(
                '.predictive-search__item[aria-selected="true"]',
            );
            if (selected) selected.setAttribute("aria-selected", "false");

            // Remove escape key handler
            if (this.keyupHandler) {
                document.removeEventListener("keyup", this.keyupHandler);
            }

            if (this.focusTrapHandler) {
                this.removeEventListener("keydown", this.focusTrapHandler);
            }

            if (this.focusOutHandler) {
                this.removeEventListener("focusout", this.focusOutHandler);
            }

            this.resetBtn.hidden = true;
            if (this.recentlyViewed) {
                if (this.recentlyViewed.hidden === true)
                    this.recentlyViewed.hidden = false;
            }

            this.resetBtn.removeEventListener(
                "click",
                this.resetBtnClickHandler,
            );
            this.overlay.removeEventListener("click", this.overlayClickHandler);
        }

        handleFocusTrap(evt) {
            if (evt.key !== "Tab" && evt.keyCode !== 9) {
                return;
            }

            const focusableSelectors =
                'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';

            const focusableElements = Array.from(
                this.querySelectorAll(focusableSelectors),
            ).filter(
                (el) =>
                    el.offsetParent !== null && !el.hasAttribute("disabled"),
            );

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (evt.shiftKey) {
                if (document.activeElement === firstElement) {
                    evt.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    evt.preventDefault();
                    firstElement.focus();
                }
            }
        }

        handleFocusOut(evt) {
            // evt.relatedTarget: The element receiving the new focus.
            const newFocusElement = evt.relatedTarget;

            // If a new element is focused AND it is NOT within this search component:
            if (newFocusElement && !this.contains(newFocusElement)) {
                this.closeModal();
            }
        }

        /**
         * Gets the value of the search input field in modal.
         * @returns {string}
         */
        getQuery() {
            return this.input?.value?.trim() || "";
        }

        /**
         * Handles search form submit.
         * @param {object} evt - Event object.
         */
        handleSubmit(evt) {
            const query = this.getQuery();            
            if (!query.length || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')) {
                evt.preventDefault();
                return false;
            }
            // Allow form submission to search page
            return true;
        }

        /**
         * Handles modal form submit.
         * @param {object} evt - Event object.
         */
        handleModalSubmit(evt) {
            const query = this.getQuery();
            if (!query.length) {
                evt.preventDefault();
                return false;
            }
            // Allow form submission to search page
            return true;
        }

        /**
         * Handles 'input' events on the search field.
         */
        handleInput() {
            this.input.setAttribute("value", this.input.value);
            const searchTerm = this.getQuery();

            if (!searchTerm.length) {
                if (this.results) {
                    this.results.innerHTML = this.cachedResults.initialResults;
                }
                if (this.modalResetBtn) {
                    this.modalResetBtn.hidden = true;
                }
                return;
            }

            if (this.modalResetBtn) {
                this.modalResetBtn.hidden = false;
            }
            if (
                this.resetBtn &&
                this.resetBtn.hidden &&
                this.getQuery().length
            ) {
                this.resetBtn.hidden = false;
            }

            this.getResults(searchTerm);
        }

        /**
         * Handles 'keydown' events on the search field.
         * @param {object} evt - Event object.
         */
        handleKeydown(evt) {
            switch (evt.key) {
                case "Escape":
                    this.closeModal();
                    break;
                // Add more keyboard navigation if needed
            }
        }

        /**
         * Handles the reset button being clicked
         */
        handleReset() {
            if (this.modalInput) {
                this.modalInput.value = "";
                this.modalInput.focus();
            }
            if (this.results) {
                this.results.innerHTML = "";
            }
            if (this.modalResetBtn) {
                this.modalResetBtn.hidden = true;
            }
        }

        /**
         * Gets the results for a search term using Shopify AJAX API.
         * @param {string} searchTerm - Search query.
         */
        async getResults(searchTerm) {
            this.setLiveRegionLoadingState();

            const searchFields =
                "title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag";

            let searchParams = "";
            if (this.productTypeInput && this.productTypeInput.value !== "") {
                searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
            } else {
                searchParams = `q=${encodeURIComponent(searchTerm)}`;
            }

            searchParams += "&section_id=predictive-search";
            searchParams += `&${encodeURIComponent("resources[type]")}=product,collection,page,query`;
            searchParams += `&${encodeURIComponent("resources[limit]")}=10`;
            searchParams += `&${encodeURIComponent("resources[limit_scope]")}=each`;
            searchParams += `&${encodeURIComponent("resources[options][fields]")}=${searchFields}`;
            searchParams += `&${encodeURIComponent("resources[options][unavailable_products]")}=last`;

            const queryKey = searchParams.replace(" ", "-").toLowerCase();
            if (this.cachedResults[queryKey]) {
                this.renderResults(this.cachedResults[queryKey]);
                return;
            }

            try {
                const response = await fetch(
                    `${theme.routes.predictiveSearch}?${searchParams}`,
                );
                if (!response.ok) throw new Error(response.status);

                const tmpl = document.createElement("template");
                tmpl.innerHTML = await response.text();

                const resultsEl = tmpl.content.querySelector(
                    "#shopify-section-predictive-search",
                );
                const resultsMarkup = resultsEl.innerHTML.replace(
                    /psearch/g,
                    this.input.id,
                );

                this.cachedResults[queryKey] = resultsMarkup;
                this.renderResults(resultsMarkup);
            } catch (error) {
                this.close();
                throw error;
            }
        }

        async getRecentlyViewedResults() {
            try {
                const recentlyViewed = JSON.parse(
                    localStorage.getItem("cc-recently-viewed") || "[]",
                );
                const response = await fetch(
                    `/search?section_id=search-recently-viewed&q=${recentlyViewed.map((item) => `id:${item}`).join(" OR ")}`,
                );
                if (!response.ok) throw new Error(response.status);

                const tmpl = document.createElement("template");
                tmpl.innerHTML = await response.text();

                if (tmpl.innerHTML.trim()) {
                    const html = tmpl.content.querySelector(
                        "#search-recently-viewed",
                    ).innerHTML;
                    if (this.recentlyViewed) {
                        this.recentlyViewed.innerHTML = html;
                        if (this.cachedResults?.initialResults) {
                            const cachedTemp =
                                document.createElement("template");
                            cachedTemp.innerHTML =
                                this.cachedResults.initialResults;
                            const recentlyViewed =
                                cachedTemp.content.querySelector(
                                    ".recently-viewed",
                                );
                            if (recentlyViewed) {
                                this.cachedResults.initialResults =
                                    cachedTemp.innerHTML;
                            }
                        }
                    }
                }
            } catch (error) {
                console.log(error);
            }
        }

        /**
         * Sets the live region loading state.
         */
        setLiveRegionLoadingState() {
            this.setLiveRegionText(this.loadingText);
        }

        /**
         * Sets the live region text.
         * @param {string} statusText - Status text.
         */
        setLiveRegionText(statusText) {
            if (!this.statusEl) return;

            this.statusEl.setAttribute("aria-hidden", "false");
            this.statusEl.textContent = statusText;

            setTimeout(() => {
                this.statusEl.setAttribute("aria-hidden", "true");
            }, 1000);
        }

        /**
         * Renders the results markup.
         * @param {string} resultsMarkup - Results markup.
         */
        renderResults(resultsMarkup) {
            console.log(this.results);
            if (!this.results) return;

            this.results.innerHTML = resultsMarkup;
            this.setLiveRegionText("");
        }

        /**
         * Renders no results message.
         * @param {string} searchTerm - Search query.
         */
        renderNoResults(searchTerm) {
            if (!this.results) return;

            this.results.innerHTML = `
          <div class="predictive-search__no-results">
            ${theme.strings.search.no_results.replace("{terms}", searchTerm)}
          </div>
        `;
        }

        /**
         * Renders error message.
         */
        renderError() {
            if (!this.results) return;

            this.results.innerHTML = `
          <div class="predictive-search__no-results">
            ${theme.strings.search.error || "An error occurred. Please try again."}
          </div>
        `;
        }
    }

    customElements.define("custom-search", CustomSearch);
}
